import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["className", "id", "mode", "columns", "emptyState", "fetchData", "data", "renderItem", "action", "isLoading", "isSortable", "isSearchable", "isFilterable", "shouldHideHeader", "hasTotal", "hasPagination", "hasEmptyResult", "isManualPagination", "isManualSort", "pageSizeOptions", "defaultPageSize", "searchText", "showPerPageText", "clearFiltersText", "selectedText", "filteredText", "filtersText", "selectText", "rowText", "rowsText", "hasViewMode", "defaultMode", "noOptionsText", "noResultsText", "gridColumns", "pageText", "ofText", "allText", "allSelectedText", "someSelectedText", "total", "pageCount", "pageIndex", "sortBy", "search", "filters", "onSearchChange", "onFiltersChange", "onPageIndexChange", "onSortChange"],
    _excluded2 = ["className", "title", "metadata", "media", "children", "actions", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useState, useMemo, forwardRef, Fragment } from 'react';
import { ThemeProvider } from 'styled-components';
import cx from 'classnames';
import { prefix } from '../../tools/settings';
import Card from '../Card';
import Icon from '../Icon';
import Button from '../Button';
import Stack from '../Stack';
import Dropdown from '../Dropdown';
import IconButton from '../IconButton';
import Link from '../Link';
import Popover from '../Popover';
import Select from '../Select';
import FormField from '../FormField';
import Skeleton from '../Skeleton';
import EmptyState from '../EmptyState';
import Paragraph from '../Paragraph';
import SegmentedControl from '../SegmentedControl';
import { useTable, usePagination, useSortBy, useFilters, useGlobalFilter, useMountedLayoutEffect } from 'react-table';
import { Styled } from './List2.styles';
import { List2Context } from './List2.context';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var List2ColumnFilter = function List2ColumnFilter(_ref) {
  var label = _ref.label,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'single' : _ref$type,
      placeholder = _ref.placeholder,
      controlledOptions = _ref.options,
      selectText = _ref.selectText,
      noOptionsText = _ref.noOptionsText,
      allSelectedText = _ref.allSelectedText,
      someSelectedText = _ref.someSelectedText,
      onMenuClose = _ref.onMenuClose,
      isManualPagination = _ref.isManualPagination,
      _ref$column = _ref.column,
      filterValue = _ref$column.filterValue,
      setFilter = _ref$column.setFilter,
      preFilteredRows = _ref$column.preFilteredRows,
      id = _ref$column.id;
  var options = useMemo(function () {
    var options = new Set();

    if (!isManualPagination) {
      preFilteredRows.forEach(function (row) {
        options.add(row.values[id]);
      });
      return _toConsumableArray(options.values()).sort();
    } else {
      return options;
    }
  }, [id, isManualPagination, preFilteredRows]);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      selectedOption = _useState2[0],
      setSelectedOption = _useState2[1];

  useEffect(function () {
    if (filterValue === undefined) {
      setSelectedOption(null);
    }
  }, [filterValue]);

  var single = _jsx(Dropdown, {
    id: id,
    value: filterValue || '',
    onChange: function onChange(e) {
      setFilter(e.target.value || undefined);
    },
    style: {
      marginLeft: 8
    },
    label: label,
    placeholder: placeholder,
    children: controlledOptions ? controlledOptions.map(function (option, i) {
      return _jsx(Dropdown.Option, {
        value: option.value,
        children: option.label
      }, i);
    }) : options.map(function (option, i) {
      return _jsx(Dropdown.Option, {
        value: option,
        children: option
      }, i);
    })
  });

  var multiple = _jsx(FormField, {
    label: label,
    children: _jsx(Select, {
      id: id,
      hideSelectedOptions: true,
      isClearable: true,
      value: selectedOption,
      placeholder: selectText,
      menuPortalTarget: document.body,
      allSelectedMessage: allSelectedText,
      someSelectedMessage: someSelectedText,
      noOptionsMessage: function noOptionsMessage() {
        return "".concat(noOptionsText);
      },
      options: controlledOptions ? controlledOptions : options.map(function (option, i) {
        return {
          label: option,
          value: option
        };
      }),
      onMenuClose: onMenuClose,
      onChange: function onChange(selected, event) {
        setSelectedOption(selected);
        setFilter((selected === null || selected === void 0 ? void 0 : selected.value) || undefined);
      }
    })
  });

  return type === 'single' ? single : multiple;
};

var List2Filter = function List2Filter(_ref2) {
  var preGlobalFilteredRows = _ref2.preGlobalFilteredRows,
      preFilteredRows = _ref2.preFilteredRows,
      globalFilter = _ref2.globalFilter,
      setGlobalFilter = _ref2.setGlobalFilter,
      i18n = _ref2.i18n,
      hasTotal = _ref2.hasTotal,
      total = _ref2.total,
      isManualPagination = _ref2.isManualPagination,
      isSearchable = _ref2.isSearchable,
      onSearchChange = _ref2.onSearchChange,
      setAllFilters = _ref2.setAllFilters,
      search = _ref2.search,
      filters = _ref2.filters,
      rows = _ref2.rows;
  var count = preFilteredRows.length;
  return _jsxs(_Fragment, {
    children: [isManualPagination ? _jsx("div", {
      children: hasTotal && _jsxs("div", {
        children: [total, ' ', (search || filters.length > 0) && _jsx("span", {
          className: "filtered",
          children: i18n.filtered
        }), ' ', total <= 1 ? i18n.row : i18n.rows, ' ', (search || filters.length > 0) && _jsx(Link, {
          style: {
            marginLeft: 8
          },
          onClick: function onClick() {
            onSearchChange && onSearchChange('');
            setAllFilters([]);
            setGlobalFilter(null);
          },
          children: i18n.clearFilters
        })]
      })
    }) : _jsx("div", {
      children: hasTotal && _jsxs("div", {
        children: [rows, ' ', rows < count && _jsx("span", {
          className: "filtered",
          children: i18n.filtered
        }), ' ', rows <= 1 ? i18n.row : i18n.rows, ' ', rows < count && _jsx(Link, {
          style: {
            marginLeft: 8
          },
          onClick: function onClick() {
            onSearchChange && onSearchChange('');
            setGlobalFilter(null);
            setAllFilters([]);
          },
          children: i18n.clearFilters
        })]
      })
    }), isSearchable && _jsx(Styled.List2Search, {
      value: isManualPagination ? search : globalFilter || '',
      placeholder: i18n.search,
      onChange: function onChange(e) {
        onSearchChange && onSearchChange(e.target.value);
        !isManualPagination && setGlobalFilter(e.target.value || undefined);
      }
    })]
  });
};

var List2 = forwardRef(function (_ref3, ref) {
  var _cx;

  var customClassName = _ref3.className,
      id = _ref3.id,
      mode = _ref3.mode,
      columns = _ref3.columns,
      emptyState = _ref3.emptyState,
      fetchData = _ref3.fetchData,
      data = _ref3.data,
      renderItem = _ref3.renderItem,
      action = _ref3.action,
      _ref3$isLoading = _ref3.isLoading,
      isLoading = _ref3$isLoading === void 0 ? false : _ref3$isLoading,
      _ref3$isSortable = _ref3.isSortable,
      isSortable = _ref3$isSortable === void 0 ? false : _ref3$isSortable,
      _ref3$isSearchable = _ref3.isSearchable,
      isSearchable = _ref3$isSearchable === void 0 ? false : _ref3$isSearchable,
      _ref3$isFilterable = _ref3.isFilterable,
      isFilterable = _ref3$isFilterable === void 0 ? false : _ref3$isFilterable,
      _ref3$shouldHideHeade = _ref3.shouldHideHeader,
      shouldHideHeader = _ref3$shouldHideHeade === void 0 ? false : _ref3$shouldHideHeade,
      _ref3$hasTotal = _ref3.hasTotal,
      hasTotal = _ref3$hasTotal === void 0 ? false : _ref3$hasTotal,
      _ref3$hasPagination = _ref3.hasPagination,
      hasPagination = _ref3$hasPagination === void 0 ? false : _ref3$hasPagination,
      _ref3$hasEmptyResult = _ref3.hasEmptyResult,
      hasEmptyResult = _ref3$hasEmptyResult === void 0 ? false : _ref3$hasEmptyResult,
      _ref3$isManualPaginat = _ref3.isManualPagination,
      isManualPagination = _ref3$isManualPaginat === void 0 ? false : _ref3$isManualPaginat,
      _ref3$isManualSort = _ref3.isManualSort,
      isManualSort = _ref3$isManualSort === void 0 ? false : _ref3$isManualSort,
      _ref3$pageSizeOptions = _ref3.pageSizeOptions,
      pageSizeOptions = _ref3$pageSizeOptions === void 0 ? [10, 20, 30, 50, 100] : _ref3$pageSizeOptions,
      _ref3$defaultPageSize = _ref3.defaultPageSize,
      defaultPageSize = _ref3$defaultPageSize === void 0 ? 10 : _ref3$defaultPageSize,
      _ref3$searchText = _ref3.searchText,
      searchText = _ref3$searchText === void 0 ? 'Search' : _ref3$searchText,
      _ref3$showPerPageText = _ref3.showPerPageText,
      showPerPageText = _ref3$showPerPageText === void 0 ? 'Show per page' : _ref3$showPerPageText,
      _ref3$clearFiltersTex = _ref3.clearFiltersText,
      clearFiltersText = _ref3$clearFiltersTex === void 0 ? 'Clear filters' : _ref3$clearFiltersTex,
      _ref3$selectedText = _ref3.selectedText,
      selectedText = _ref3$selectedText === void 0 ? 'selected' : _ref3$selectedText,
      _ref3$filteredText = _ref3.filteredText,
      filteredText = _ref3$filteredText === void 0 ? 'filtered' : _ref3$filteredText,
      _ref3$filtersText = _ref3.filtersText,
      filtersText = _ref3$filtersText === void 0 ? 'Filters' : _ref3$filtersText,
      _ref3$selectText = _ref3.selectText,
      selectText = _ref3$selectText === void 0 ? 'Select...' : _ref3$selectText,
      _ref3$rowText = _ref3.rowText,
      rowText = _ref3$rowText === void 0 ? 'row' : _ref3$rowText,
      _ref3$rowsText = _ref3.rowsText,
      rowsText = _ref3$rowsText === void 0 ? 'rows' : _ref3$rowsText,
      _ref3$hasViewMode = _ref3.hasViewMode,
      hasViewMode = _ref3$hasViewMode === void 0 ? false : _ref3$hasViewMode,
      _ref3$defaultMode = _ref3.defaultMode,
      defaultMode = _ref3$defaultMode === void 0 ? 'list' : _ref3$defaultMode,
      _ref3$noOptionsText = _ref3.noOptionsText,
      noOptionsText = _ref3$noOptionsText === void 0 ? null : _ref3$noOptionsText,
      _ref3$noResultsText = _ref3.noResultsText,
      noResultsText = _ref3$noResultsText === void 0 ? {
    title: 'No results found',
    description: 'Try adjusting your search or filter to find what you are looking for.'
  } : _ref3$noResultsText,
      _ref3$gridColumns = _ref3.gridColumns,
      gridColumns = _ref3$gridColumns === void 0 ? 3 : _ref3$gridColumns,
      _ref3$pageText = _ref3.pageText,
      pageText = _ref3$pageText === void 0 ? 'Page' : _ref3$pageText,
      _ref3$ofText = _ref3.ofText,
      ofText = _ref3$ofText === void 0 ? 'of' : _ref3$ofText,
      _ref3$allText = _ref3.allText,
      allText = _ref3$allText === void 0 ? 'All' : _ref3$allText,
      allSelectedText = _ref3.allSelectedText,
      someSelectedText = _ref3.someSelectedText,
      total = _ref3.total,
      controlledPageCount = _ref3.pageCount,
      _ref3$pageIndex = _ref3.pageIndex,
      controlledPageIndex = _ref3$pageIndex === void 0 ? 0 : _ref3$pageIndex,
      _ref3$sortBy = _ref3.sortBy,
      controlledSortBy = _ref3$sortBy === void 0 ? [] : _ref3$sortBy,
      controlledSearch = _ref3.search,
      _ref3$filters = _ref3.filters,
      controlledFilters = _ref3$filters === void 0 ? [] : _ref3$filters,
      onSearchChange = _ref3.onSearchChange,
      onFiltersChange = _ref3.onFiltersChange,
      onPageIndexChange = _ref3.onPageIndexChange,
      onSortChange = _ref3.onSortChange,
      rest = _objectWithoutProperties(_ref3, _excluded);

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-list2"), true), _defineProperty(_cx, customClassName, !!customClassName), _cx));

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      popoverVisible = _useState4[0],
      setPopoverVisible = _useState4[1];

  var showPopover = function showPopover() {
    return setPopoverVisible(true);
  };

  var hidePopover = function hidePopover() {
    return setPopoverVisible(false);
  };

  var _useState5 = useState(defaultMode),
      _useState6 = _slicedToArray(_useState5, 2),
      view = _useState6[0],
      setView = _useState6[1];

  var context = {};
  var defaultColumn = useMemo(function () {
    return {
      Filter: List2ColumnFilter
    };
  }, []);

  var _useTable = useTable({
    defaultColumn: defaultColumn,
    columns: columns,
    data: data,
    sortTypes: useMemo(function () {
      return {
        alphanumeric: function alphanumeric(a, b, id, desc) {
          var valueA = String(a.values[id]).toLowerCase();
          var valueB = String(b.values[id]).toLowerCase();

          if (valueA > valueB) {
            return 1;
          } else if (valueA < valueB) {
            return -1;
          } else {
            return 0;
          }
        }
      };
    }, []),
    disableFilters: !isFilterable,
    disableGlobalFilter: isManualPagination,
    manualPagination: isManualPagination,
    manualSortBy: isManualSort,
    manualFilters: isManualPagination,
    pageCount: controlledPageCount,
    autoResetPage: isManualPagination ? false : true,
    initialState: {
      globalFilter: !isManualPagination && controlledSearch,
      sortBy: controlledSortBy,
      filters: controlledFilters,
      pageIndex: controlledPageIndex || 0,
      pageSize: hasPagination ? defaultPageSize : data.length
    }
  }, useFilters, useGlobalFilter, useSortBy, usePagination),
      rows = _useTable.rows,
      prepareRow = _useTable.prepareRow,
      page = _useTable.page,
      allColumns = _useTable.allColumns,
      canPreviousPage = _useTable.canPreviousPage,
      canNextPage = _useTable.canNextPage,
      pageOptions = _useTable.pageOptions,
      gotoPage = _useTable.gotoPage,
      nextPage = _useTable.nextPage,
      previousPage = _useTable.previousPage,
      setPageSize = _useTable.setPageSize,
      setSortBy = _useTable.setSortBy,
      preGlobalFilteredRows = _useTable.preGlobalFilteredRows,
      preFilteredRows = _useTable.preFilteredRows,
      setGlobalFilter = _useTable.setGlobalFilter,
      setAllFilters = _useTable.setAllFilters,
      _useTable$state = _useTable.state,
      pageSize = _useTable$state.pageSize,
      pageIndex = _useTable$state.pageIndex,
      globalFilter = _useTable$state.globalFilter,
      filters = _useTable$state.filters,
      sortBy = _useTable$state.sortBy;

  useMountedLayoutEffect(function () {
    onPageIndexChange && onPageIndexChange(pageIndex);
  }, [onPageIndexChange, pageIndex]);
  useEffect(function () {
    gotoPage(controlledPageIndex);
  }, [gotoPage, controlledPageIndex]);
  useEffect(function () {
    if (isManualPagination) {
      fetchData({
        pageIndex: pageIndex,
        pageSize: pageSize
      });
    }
  }, [fetchData, pageIndex, pageSize, isManualPagination]);
  useMountedLayoutEffect(function () {
    onSortChange && onSortChange(sortBy);
  }, [onSortChange, sortBy]);
  useEffect(function () {
    onSortChange && setSortBy(controlledSortBy);
  }, [onSortChange, setSortBy, controlledSortBy]);
  useMountedLayoutEffect(function () {
    onFiltersChange && onFiltersChange(filters);
  }, [onFiltersChange, filters]);
  useMountedLayoutEffect(function () {
    onPageIndexChange && onPageIndexChange(pageIndex);
  }, [onPageIndexChange, pageIndex]);
  useMountedLayoutEffect(function () {
    if (onSearchChange && controlledSearch) {
      onSearchChange(controlledSearch);
    }
  }, [onSearchChange, controlledSearch]);
  useEffect(function () {
    if (!isManualPagination) {
      setGlobalFilter(controlledSearch);
    }
  }, [isManualPagination, controlledSearch, setGlobalFilter]);
  var rowData = hasPagination ? page : rows;
  var filteredColumns = allColumns.filter(function (column) {
    return column.allowFilter === true;
  }).length;
  return _jsx(ThemeProvider, {
    theme: {
      mode: mode
    },
    children: _jsx(List2Context.Provider, {
      value: context,
      children: _jsxs(Styled.List2, _objectSpread(_objectSpread({
        ref: ref,
        className: className
      }, rest), {}, {
        children: [_jsxs(Styled.List2Head, {
          shouldHideHeader: shouldHideHeader,
          children: [_jsx(Styled.List2SearchContainer, {
            children: _jsx(List2Filter, {
              preGlobalFilteredRows: preGlobalFilteredRows,
              preFilteredRows: preFilteredRows,
              globalFilter: globalFilter,
              setGlobalFilter: setGlobalFilter,
              setAllFilters: setAllFilters,
              i18n: {
                search: searchText,
                rows: rowsText,
                row: rowText,
                filtered: filteredText,
                clearFilters: clearFiltersText
              },
              isManualPagination: isManualPagination,
              hasTotal: hasTotal,
              isSearchable: isSearchable,
              total: total,
              search: controlledSearch,
              filters: controlledFilters,
              onSearchChange: onSearchChange,
              rows: isManualPagination ? total : rows.length
            })
          }), isFilterable && _jsx(Styled.List2Filters, {
            className: "".concat(prefix, "-table-filters"),
            children: filteredColumns === 1 ? allColumns.map(function (column, i) {
              return column.allowFilter && _jsx(Fragment, {
                children: column.render('Filter', {
                  label: column.render('Header'),
                  isManualPagination: isManualPagination,
                  placeholder: column.placeholder || allText,
                  options: column.options
                })
              }, i);
            }) : _jsxs(_Fragment, {
              children: [_jsxs(Popover, {
                placement: "bottom-end",
                width: 300,
                appendTo: "parent",
                isVisible: popoverVisible,
                isCloseOnBlur: false,
                offset: [26, 15],
                trigger: "manual",
                children: [_jsx(Popover.Trigger, {
                  children: _jsxs(Styled.List2FilterButton, {
                    onClick: popoverVisible ? hidePopover : showPopover,
                    children: [filtersText, ' ', filters.length > 0 && "(".concat(filters.length, ")"), _jsx(Icon, {
                      style: {
                        marginLeft: 8
                      },
                      icon: "settings",
                      size: 14,
                      color: 'var(--dentsu-color-blue)'
                    })]
                  })
                }), _jsx(Popover.Content, {
                  children: allColumns.map(function (column, i) {
                    return column.allowFilter && _jsx(Fragment, {
                      children: column.render('Filter', {
                        label: column.render('Header'),
                        placeholder: column.placeholder || allText,
                        isManualPagination: isManualPagination,
                        selectText: selectText,
                        allSelectedText: allSelectedText,
                        someSelectedText: someSelectedText,
                        options: column.options,
                        noOptionsText: noOptionsText || column.noOptionsText || 'No options',
                        type: 'multiple'
                      })
                    }, i);
                  })
                })]
              }), popoverVisible && _jsx("div", {
                onClick: hidePopover,
                style: {
                  position: 'fixed',
                  background: 'transparent',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  zIndex: 9998
                }
              })]
            })
          }), (isFilterable || isSearchable) && hasViewMode && _jsx(Styled.List2HeadSeparator, {}), hasViewMode && _jsxs(SegmentedControl, {
            isIconOnly: true,
            onChange: function onChange(e) {
              return setView(e.target.value);
            },
            value: view,
            children: [_jsx(SegmentedControl.Button, {
              value: "grid",
              icon: "layout-grid",
              children: "Grid"
            }), _jsx(SegmentedControl.Button, {
              value: "list",
              icon: "properties",
              children: "List"
            })]
          }), (isFilterable || isSearchable || hasViewMode) && action && _jsx(Styled.List2HeadSeparator, {}), action && _jsx(Styled.List2HeadAction, {
            className: "".concat(prefix, "-list2-action"),
            children: _jsx(Button, {
              iconLeft: action.icon,
              variant: "tertiary",
              onClick: action.onClick,
              onBlur: action.onBlur,
              isDisabled: action.isDisabled,
              isLoading: action.isLoading,
              children: action.content
            })
          })]
        }), _jsxs(Styled.List2Body, {
          children: [isLoading && Array.from({
            length: defaultPageSize
          }, function (_, rowIndex) {
            return _jsxs(Styled.List2Row, {
              children: [_jsx(Styled.List2Cell, {
                image: true,
                children: _jsx(Skeleton, {
                  isCircle: true,
                  height: 40,
                  width: 40,
                  style: {
                    opacity: 1 - rowIndex / (defaultPageSize + 5)
                  }
                })
              }), _jsxs(Styled.List2Cell, {
                children: [_jsx(Styled.List2Title, {
                  children: _jsx(Skeleton, {
                    width: 150,
                    style: {
                      opacity: 1 - rowIndex / (defaultPageSize + 5)
                    }
                  })
                }), _jsx(Styled.List2Caption, {
                  children: _jsx(Skeleton, {
                    width: 100,
                    style: {
                      opacity: 1 - rowIndex / (defaultPageSize + 5)
                    }
                  })
                })]
              }), _jsx(Styled.List2Cell, {
                children: _jsx(Skeleton, {
                  width: 100,
                  style: {
                    opacity: 1 - rowIndex / (defaultPageSize + 5)
                  }
                })
              }), _jsx(Styled.List2Cell, {
                actions: true,
                children: _jsx(Skeleton, {
                  width: 40,
                  style: {
                    opacity: 1 - rowIndex / (defaultPageSize + 5)
                  }
                })
              })]
            }, rowIndex);
          }), hasEmptyResult ? !isLoading && _jsx(EmptyState, {
            layout: "vertical",
            size: "small",
            image: null,
            title: noResultsText.title,
            children: noResultsText.description && _jsx(Paragraph, {
              children: noResultsText.description
            })
          }) : rows.length === 0 && !isLoading && _jsx(_Fragment, {
            children: rows.length < preFilteredRows.length ? _jsx(EmptyState, {
              layout: "vertical",
              size: "small",
              image: null,
              title: noResultsText.title,
              children: noResultsText.description && _jsx(Paragraph, {
                children: noResultsText.description
              })
            }) : emptyState
          }), !isLoading && (view === 'grid' ? _jsx(Card.Group, {
            cols: gridColumns,
            spacing: 16,
            children: rowData.map(function (row, i) {
              if (hasPagination) prepareRow(row);
              return renderItem(row.original, i, view);
            })
          }) : rowData.map(function (row, i) {
            if (hasPagination) prepareRow(row);
            return renderItem(row.original, i, view);
          }))]
        }), hasPagination && _jsxs(Styled.List2Pagination, {
          children: [_jsx(Styled.List2PaginationOptions, {
            children: _jsxs(Stack, {
              alignItems: "center",
              children: [_jsxs(Styled.List2PaginationSelection, {
                children: [showPerPageText, ":"]
              }), _jsx(Dropdown, {
                style: {
                  marginLeft: 8,
                  marginRight: 8
                },
                value: pageSize,
                onChange: function onChange(e) {
                  setPageSize(Number(e.target.value));
                },
                children: pageSizeOptions.map(function (pageSize) {
                  return _jsx(Dropdown.Option, {
                    value: pageSize,
                    children: pageSize
                  }, pageSize);
                })
              })]
            })
          }), (isManualPagination && total !== 0 || !isManualPagination && rows !== 0) && _jsx(Styled.List2PaginationNavigation, {
            children: _jsxs(Stack, {
              alignItems: "center",
              children: [pageText, _jsx(Dropdown, {
                style: {
                  marginLeft: 8,
                  marginRight: 8
                },
                value: pageIndex,
                isDisabled: pageOptions.length === 1,
                onChange: function onChange(e) {
                  gotoPage(Number(e.target.value));
                },
                children: pageOptions.fill(undefined).map(function (value, index) {
                  return _jsx(Dropdown.Option, {
                    value: index,
                    children: index + 1
                  }, index);
                })
              }), ofText, " ", pageOptions.length, _jsxs(Styled.List2PaginationButtons, {
                children: [_jsx(IconButton, {
                  icon: "arrow-left",
                  variant: "ghost",
                  size: "small",
                  isDisabled: !canPreviousPage,
                  onClick: function onClick() {
                    if (!canPreviousPage) return;
                    previousPage();
                  }
                }), _jsx(IconButton, {
                  icon: "arrow-right",
                  variant: "ghost",
                  size: "small",
                  isDisabled: !canNextPage,
                  onClick: function onClick() {
                    if (!canNextPage) return;
                    nextPage();
                  }
                })]
              })]
            })
          })]
        })]
      }))
    })
  });
});

var List2Row = function List2Row(_ref4, ref) {
  var _cx2;

  var customClassName = _ref4.className,
      title = _ref4.title,
      metadata = _ref4.metadata,
      media = _ref4.media,
      children = _ref4.children,
      actions = _ref4.actions,
      onClick = _ref4.onClick,
      rest = _objectWithoutProperties(_ref4, _excluded2);

  var className = cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "-list2-row"), true), _defineProperty(_cx2, customClassName, !!customClassName), _cx2));
  return _jsxs(Styled.List2Row, _objectSpread(_objectSpread({
    onClick: onClick,
    className: className
  }, rest), {}, {
    children: [media && _jsx(Styled.List2Cell, {
      image: true,
      children: media
    }), _jsxs(Styled.List2Cell, {
      children: [_jsx(Styled.List2Title, {
        children: title
      }), metadata && _jsx(Styled.List2Caption, {
        children: metadata
      })]
    }), children && _jsx(Styled.List2Cell, {
      children: children
    }), _jsx(Styled.List2Cell, {
      actions: true,
      children: onClick ? _jsx(Icon, {
        icon: "chevron-right",
        color: 'var(--dentsu-color-blue)'
      }) : actions
    })]
  }));
};

List2.displayName = 'List2';
List2.Row = List2Row;
List2.Card = Card;
export default List2;